import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import './CopyButton.css';

// Copy-to-clipboard button with "Copied" feedback.
//
// Extracted because the same navigator.clipboard.writeText + setCopied + setTimeout block had been
// hand-written in eight components, each with its own timeout and its own (or no) failure handling.
//
// `value` for something already in hand; `getValue` for something that has to be fetched first (an
// async function returning the string). When the clipboard write is refused - it fails on an insecure
// origin, and browsers may reject it after an await has consumed the click's user-activation -
// onFallback receives the value so the caller can show it for manual copying. A copy button that
// silently does nothing is worse than no button, because the user walks away believing they have it.
const CopyButton = ({
    value,
    getValue,
    label = 'Copy link',
    copiedLabel = 'Copied',
    title,
    className = '',
    disabled = false,
    onFallback,
}) => {
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    const handleCopy = async () => {
        setBusy(true);
        let resolved = value;
        try {
            if (typeof getValue === 'function') resolved = await getValue();
            if (!resolved) throw new Error('nothing to copy');
            await navigator.clipboard.writeText(resolved);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            if (onFallback) onFallback(resolved || null, e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            className={`copy-btn ${className}`.trim()}
            onClick={handleCopy}
            disabled={disabled || busy}
            title={title}
        >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? copiedLabel : label}
        </button>
    );
};

export default CopyButton;
